import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, ArrowLeft, Check, BookOpen, CalendarClock, Mic, Sparkles,
  ShieldCheck, Users, FileText, Award, Phone, MessageCircle, Timer, Quote,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import { COURSE_LIST } from "@/data/courses";
import BookingDialog from "@/components/site/BookingDialog";
import TrackedPhoneLink from "@/components/site/TrackedPhoneLink";
import { trackViewItem } from "@/lib/analytics";

const MODULES = [
  { title: "Warum KI in der Pflege-Doku?", desc: "Rechtslage, MD-Prüfung & Halluzinationsschutz — warum die KI Lücken benennt statt sie zu erfinden.", merksatz: "Die KI schreibt. Du entscheidest.", icon: ShieldCheck },
  { title: "Die digitale Patientenmappe", desc: "Alle 12 Sektionen sicher anlegen & pflegen — von Stammdaten bis Freigabe.", merksatz: "Eine Mappe = ein Mensch = 12 Schubladen.", icon: FileText },
  { title: "Diktieren wie ein Profi", desc: "Whisper-Spracheingabe am Patientenbett: kurze Sätze, echte Beobachtungen — die KI glättet.", merksatz: "Sprich, was du siehst. Kurz und ehrlich. Die KI macht es schön.", icon: Mic },
  { title: "Die perfekte SIS mit KI", desc: "Vom Diktat zur strukturierten SIS: alle 6 Themenfelder auf Knopfdruck — mit fachlichem Gegenlesen.", merksatz: "Einmal erzählen — sechs Felder füllen. Aber: Du liest immer gegen.", icon: Sparkles },
  { title: "Maßnahmenplan & Risikomatrix", desc: "KI-Vorschläge prüfen & übernehmen, Risiko-Ampel verstehen, nichts vergessen.", merksatz: "SIS = Zutaten. Maßnahmenplan = Rezept. Rotes Risiko = Rezept nötig!", icon: Check },
  { title: "Pflegegrad & Höherstufung", desc: "NBA-Analyse, Tendenz-Einschätzung & fertiger Kassenbrief für die Höherstufung.", merksatz: "Die KI zeigt die Tendenz. Der MD entscheidet. Der Mensch bekommt, was ihm zusteht.", icon: Award },
  { title: "Die MD-Prüfung bestehen", desc: "QPR-26 Checkliste, MD-Simulator & Cross-Doc-Plausibilität — die Generalprobe vor dem Ernstfall.", merksatz: "Erst die Generalprobe, dann die Aufführung. Der Simulator findet die Fehler — nicht der Prüfer.", icon: ShieldCheck },
  { title: "Führen mit Daten", desc: "Freigaben, Team-Verwaltung, Wochen- & Monatsreport — das PDL-Modul.", merksatz: "Erst prüfen, dann unterschreiben, dann PDF. Deine Unterschrift = deine Verantwortung.", icon: Users },
];

const GOLDENE_REGELN = [
  { t: "Die KI schreibt, du entscheidest.", d: "Kein Dokument verlässt das Haus ohne fachliche Prüfung und Freigabe." },
  { t: "Nur echte Beobachtungen eingeben.", d: "Die KI kann nur so gut sein wie deine Eingabe." },
  { t: "Beobachtung statt Diagnose.", d: "Das System warnt — aber die Regel muss sitzen." },
  { t: "Keine Panik vor leeren Feldern.", d: "Der Halluzinationsschutz benennt Lücken, statt sie zu erfinden." },
  { t: "Freigabe = Verantwortung.", d: "Die digitale Unterschrift ist rechtlich deine." },
];

const FALL_STEPS = [
  { s: "Neue Mappe „Erika Meier“, PG 3 anlegen", min: 1 },
  { s: "Stammdaten eintragen", min: 3 },
  { s: "Situationseinschätzung diktieren (Hausbesuch nacherzählen)", min: 3 },
  { s: "SIS: Themenfelder 1–6 „Mit KI generieren“ + fachlich prüfen", min: 8 },
  { s: "Risikomatrix ankreuzen — Ampel wird rot/gelb", min: 2 },
  { s: "Maßnahmenplan: Vorschläge übernehmen & anpassen", min: 4 },
  { s: "QPR-26 durchgehen — was ist noch offen?", min: 2 },
  { s: "Zur Prüfung geben → PDL unterschreibt am Bildschirm", min: 1 },
  { s: "PDF herunterladen — fertige Akte in der Hand", min: 1 },
];

export default function PflegeKiFuehrerschein() {
  const course = COURSE_LIST.find((c) => c.id === "pflege-ki-fuehrerschein");
  const [bookingOpen, setBookingOpen] = useState(false);

  useEffect(() => {
    document.title = "Pflege-KI Führerschein · E-Learning + 6 Monate Software | Dickies-Campus";
    window.scrollTo(0, 0);
    trackViewItem("pflege-ki-fuehrerschein", "Pflege-KI Führerschein", 1990);
  }, []);

  return (
    <div className="min-h-screen bg-cream-50 text-sand-800">
      <Header />
      <main className="pb-24 lg:pb-32">
        {/* ── Hero · Desenio ── */}
        <section className="relative overflow-hidden" style={{ backgroundColor: "#F6E8DC" }}>
          <div aria-hidden="true" className="absolute -top-24 -right-24 h-96 w-96 rounded-full bg-coral/15 blur-3xl pointer-events-none" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-36 pb-16 lg:pt-44 lg:pb-24">
            <Link to="/#kurse" data-testid="fs-back-link" className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] font-semibold text-sand-500 hover:text-terra transition-colors mb-8">
              <ArrowLeft className="h-3.5 w-3.5" /> Zur Kursübersicht
            </Link>
            <div className="flex items-center gap-3 flex-wrap mb-6">
              <span className="font-poster text-[11px] tracking-[0.3em] uppercase font-bold text-terra">E-Learning · Sofort starten</span>
              <span className="h-px w-12 bg-terra/50" />
              <span className="inline-flex items-center gap-1.5 bg-terra text-cream-50 text-[10px] font-bold uppercase tracking-[0.14em] px-3.5 py-1.5 rounded-full">
                inkl. 6 Monate Pflege-KI Kompakt · Wert 1.194 €
              </span>
            </div>
            <h1 data-testid="fs-hero-title" className="font-magazine font-medium text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-sand-800 max-w-4xl">
              Der Pflege-KI <span className="italic text-terra">Führerschein</span>.
            </h1>
            <p className="mt-5 font-magazine italic text-lg md:text-2xl text-sand-600 max-w-2xl leading-snug">
              Diktieren statt tippen. SIS auf Knopfdruck. MD-Prüfung vorab simulieren.
            </p>
            <p className="mt-6 text-sand-700 text-[15px] leading-[1.75] max-w-2xl">
              {course?.description}
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-3">
              <Button onClick={() => setBookingOpen(true)} data-testid="fs-hero-buy-cta"
                className="rounded-full bg-terra hover:bg-terra-dark text-cream-50 h-14 px-8 text-[13px] font-bold uppercase tracking-[0.12em] group transition-colors">
                Jetzt buchen · € 1.990
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" strokeWidth={2.2} />
              </Button>
              <Button asChild variant="outline" data-testid="fs-hero-modules-cta"
                className="rounded-full h-14 px-8 text-[13px] font-bold uppercase tracking-[0.12em] border-sand-800/40 text-sand-800 hover:bg-white/60 bg-transparent">
                <a href="#module">Die 8 Module ansehen</a>
              </Button>
            </div>
            <p data-testid="fs-raten-hint" className="mt-5 inline-flex items-center gap-2 text-[13px] text-sand-600">
              <CalendarClock className="h-4 w-4 text-terra" strokeWidth={1.8} />
              Ratenzahlung ohne Aufschlag: ab <strong className="text-sand-800">165,83 €/Monat</strong> · 3, 6 oder 12 Raten
            </p>

            {/* Stat-Strip */}
            <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-6 border-t border-sand-800/15 pt-8 max-w-4xl">
              <Stat label="Kursformat" value="8 Module · E-Learning" />
              <Stat label="Software inklusive" value="6 Monate · bis 10 Mitarbeiter" />
              <Stat label="Erstaufnahme" value="25 statt 120 Minuten" />
              <Stat label="Abschluss" value="Zertifikat Pflege-KI Führerschein" />
            </div>
          </div>
        </section>

        {/* ── Module ── */}
        <section id="module" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 lg:pt-28">
          <SectionHead n="01" eyebrow="Modul-Vorschau" title="8 Module. Vom ersten Klick bis zum PDL-Report." />
          <div className="mt-12 grid md:grid-cols-2 gap-5 lg:gap-6">
            {MODULES.map((m, i) => (
              <div key={m.title} data-testid={`fs-module-card-${i + 1}`}
                className="group rounded-2xl bg-white border border-sand-800/10 p-6 md:p-7 hover:border-terra/40 transition-colors">
                <div className="flex items-center justify-between mb-4">
                  <span className="font-mono text-[11px] tracking-[0.2em] text-terra">MODUL {String(i + 1).padStart(2, "0")}</span>
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-terra/10 text-terra">
                    <m.icon className="h-4 w-4" strokeWidth={1.8} />
                  </span>
                </div>
                <h3 className="font-magazine text-[1.35rem] leading-[1.15] text-sand-800">{m.title}</h3>
                <p className="mt-2.5 text-[13.5px] text-sand-600 leading-[1.65]">{m.desc}</p>
                <div className="mt-4 border-l-2 border-terra/40 pl-3.5">
                  <div className="text-[9px] uppercase tracking-[0.26em] text-sand-500 font-bold">Merksatz</div>
                  <p className="mt-1 font-magazine italic text-[14.5px] text-terra leading-snug">„{m.merksatz}“</p>
                </div>
              </div>
            ))}
          </div>
          <p className="mt-6 text-[13px] text-sand-500">
            Jedes Modul: Lektionstext · Schritt-für-Schritt-Anleitung in der echten Software · Praxisaufgabe · Quiz. Am Ende: Abschlussprüfung + Zertifikat.
          </p>
        </section>

        {/* ── Handbuch-Auszug: Fallbeispiel ── */}
        <section className="mt-24 lg:mt-32 py-20 lg:py-24" style={{ backgroundColor: "#EFEDE3" }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHead n="02" eyebrow="Auszug aus dem Praxis-Handbuch" title="Erstaufnahme „Frau Meier“ — in 25 Minuten." />
            <div className="mt-12 grid lg:grid-cols-12 gap-8 lg:gap-12 items-start">
              <div className="lg:col-span-7">
                <ol className="space-y-0" data-testid="fs-fallbeispiel-steps">
                  {FALL_STEPS.map((st, i) => (
                    <li key={st.s} className="flex items-baseline gap-4 py-3 border-b border-sand-800/10">
                      <span className="font-mono text-[11px] tracking-[0.1em] text-terra shrink-0 w-6">{String(i + 1).padStart(2, "0")}</span>
                      <span className="text-[14px] text-sand-700 leading-snug flex-1">{st.s}</span>
                      <span className="font-mono text-[11.5px] text-sand-500 shrink-0">{st.min} Min</span>
                    </li>
                  ))}
                </ol>
              </div>
              <aside className="lg:col-span-5">
                <div className="rounded-2xl bg-sand-800 text-cream-100 p-7 md:p-8 lg:sticky lg:top-32" data-testid="fs-fallbeispiel-result">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-saffron text-espresso mb-5">
                    <Timer className="h-5 w-5" strokeWidth={2} />
                  </span>
                  <div className="font-magazine text-[2.6rem] leading-none">~25 Min</div>
                  <p className="mt-3 text-[14px] text-cream-100/85 leading-[1.7]">
                    Eine vollständige, MD-sichere Erstaufnahme — für die man klassisch <strong className="text-cream-50">2–3 Stunden</strong> am Schreibtisch gesessen hätte.
                  </p>
                  <p className="mt-4 text-[12px] text-cream-100/60">
                    Das durchgängige Fallbeispiel aus dem Kurs — jeder Schritt wird in der echten Software geübt.
                  </p>
                </div>
              </aside>
            </div>
          </div>
        </section>

        {/* ── Handbuch-Auszug: 5 goldene Regeln ── */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-24 lg:mt-32">
          <SectionHead n="03" eyebrow="Prüfungsstoff für den Führerschein" title="Die 5 goldenen Regeln." />
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {GOLDENE_REGELN.map((r, i) => (
              <div key={r.t} data-testid={`fs-regel-${i + 1}`}
                className={`rounded-2xl p-6 md:p-7 ${i === 0 ? "bg-coral text-cream-100" : "bg-white border border-sand-800/10"}`}>
                <div className={`font-magazine text-[2rem] leading-none ${i === 0 ? "text-cream-100/70" : "text-terra/50"}`}>{i + 1}</div>
                <h3 className={`mt-3 font-magazine text-[1.2rem] leading-[1.2] ${i === 0 ? "text-cream-50" : "text-sand-800"}`}>{r.t}</h3>
                <p className={`mt-2 text-[13.5px] leading-[1.6] ${i === 0 ? "text-cream-100/85" : "text-sand-600"}`}>{r.d}</p>
              </div>
            ))}
            <div className="rounded-2xl border-2 border-dashed border-terra/40 p-6 md:p-7 flex flex-col justify-center">
              <Quote className="h-5 w-5 text-terra mb-3" strokeWidth={1.8} />
              <p className="font-magazine italic text-[15.5px] text-sand-700 leading-snug">
                Aus dem 200-seitigen Praxis-Handbuch, das jede:r Teilnehmer:in als PDF erhält — Klick für Klick, zum Nachschlagen im Alltag.
              </p>
            </div>
          </div>
        </section>

        {/* ── Software inklusive ── */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-24 lg:mt-32">
          <div className="relative rounded-3xl bg-coral overflow-hidden" data-testid="fs-software-block">
            <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(120% 90% at 85% 10%, rgba(255,255,255,0.14) 0%, rgba(255,255,255,0) 55%)" }} />
            <div className="relative p-8 md:p-12 lg:p-14 grid lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-8">
                <span className="text-[11px] uppercase tracking-[0.3em] font-semibold text-cream-100">Im Kurspreis enthalten · sofort freigeschaltet</span>
                <h2 className="mt-4 font-magazine text-2xl sm:text-3xl lg:text-[2.4rem] text-cream-100 leading-[1.14]">
                  6 Monate Pflege-KI Kompakt — <span className="italic underline decoration-cream-100/60 decoration-2 underline-offset-4">die echte Software</span>, kein Demo-Sandkasten.
                </h2>
                <p className="mt-4 text-cream-100/90 text-[15px] leading-[1.7] max-w-2xl">
                  Du übst nicht in Screenshots, sondern arbeitest ab Tag 1 produktiv: 1 PDL-Zugang + bis zu 10 Mitarbeiter-Zugänge, Patientenmappen, SIS-KI, MD-Simulator und Reports — 6 Monate voll inklusive.
                </p>
                <div className="mt-6 flex flex-wrap gap-2.5">
                  <span className="inline-flex items-center gap-2 rounded-full bg-cream-100 text-coral-deep text-[13px] font-bold px-4 py-2">Wert: 1.194 €</span>
                  <span className="inline-flex items-center rounded-full bg-rosewood/35 border border-cream-100/40 text-cream-100 text-[13px] font-medium px-4 py-2">1 PDL + 10 Mitarbeiter</span>
                  <span className="inline-flex items-center rounded-full bg-sage-dark text-cream-100 text-[13px] font-semibold px-4 py-2">Zugang per E-Mail · direkt nach Buchung</span>
                </div>
              </div>
              <div className="lg:col-span-4">
                <div className="rounded-2xl bg-cream-50 text-sand-800 p-6 md:p-7">
                  <div className="text-[10px] uppercase tracking-[0.28em] text-sand-500 font-bold">Kurs + Software</div>
                  <div className="font-magazine text-[2.6rem] leading-none mt-2 text-terra">€ 1.990</div>
                  <div className="mt-2 text-[12px] text-sand-500">oder ab 165,83 €/Monat in Raten</div>
                  <Button onClick={() => setBookingOpen(true)} data-testid="fs-software-buy-cta"
                    className="mt-5 w-full rounded-full bg-terra hover:bg-terra-dark text-cream-50 h-12 text-[12.5px] font-bold uppercase tracking-[0.12em]">
                    Jetzt buchen <ArrowRight className="ml-2 h-4 w-4" strokeWidth={2.2} />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Abschluss-CTA ── */}
        <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 mt-24 lg:mt-28 text-center">
          <h2 className="font-magazine text-2xl md:text-4xl text-sand-800 leading-[1.15]">
            Noch Fragen? <span className="italic text-terra">Wir sind da.</span>
          </h2>
          <p className="mt-4 text-sand-600 text-[15px]">Kostenlos & unverbindlich — auch für Team-Konditionen ab 3 Teilnehmer:innen.</p>
          <div className="mt-7 flex flex-wrap items-center justify-center gap-x-6 gap-y-3">
            <TrackedPhoneLink source="fs-landing" className="inline-flex items-center gap-2 text-[14px] font-semibold text-sand-800 hover:text-terra transition-colors">
              <Phone className="h-4 w-4" strokeWidth={1.8} /> 0211 29273330
            </TrackedPhoneLink>
            <a href="https://wa.me/491622572545" target="_blank" rel="noreferrer" data-testid="fs-whatsapp-link"
              className="inline-flex items-center gap-2 text-[14px] font-semibold text-sand-800 hover:text-terra transition-colors">
              <MessageCircle className="h-4 w-4" strokeWidth={1.8} /> WhatsApp
            </a>
          </div>
        </section>
      </main>

      {course && <BookingDialog course={course} open={bookingOpen} onOpenChange={setBookingOpen} />}
      <Footer />
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="min-w-0">
      <div className="text-[9.5px] uppercase tracking-[0.26em] text-terra font-bold">{label}</div>
      <div className="text-[13.5px] text-sand-800 font-medium leading-snug mt-1.5">{value}</div>
    </div>
  );
}

function SectionHead({ n, eyebrow, title }) {
  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <span className="font-mono text-[11px] tracking-[0.2em] text-terra">{n}</span>
        <span className="text-[11px] uppercase tracking-[0.3em] font-semibold text-terra">{eyebrow}</span>
        <span className="h-px flex-1 bg-terra/25" />
      </div>
      <h2 className="font-magazine font-medium text-3xl md:text-[2.6rem] text-sand-800 leading-[1.12] max-w-3xl">
        {title}
      </h2>
    </div>
  );
}
